import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const MODEL_OPTIONS = [
  { id: "nano-banana", label: "Gemini Nano Banana", description: "Photorealistic — factory / product / lifestyle scenes · supports reference image" },
  { id: "gpt-image-1", label: "OpenAI GPT Image 1", description: "Editorial / infographic / art-directed compositions · text-only" },
];
const ASPECT_OPTIONS = ["1:1", "16:9", "4:3", "3:2", "9:16", "2:3", "4:5"];
const QUALITY_OPTIONS = ["low", "medium", "high"];
const MAX_REF_BYTES = 4 * 1024 * 1024;

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error || new Error("File read failed."));
    r.onload = () => {
      const s = String(r.result || "");
      const i = s.indexOf(",");
      resolve({ base64: i >= 0 ? s.slice(i + 1) : s, mime: file.type || "image/png" });
    };
    r.readAsDataURL(file);
  });

const AdminAiImageStudio = () => {
  const { user, logout } = useAuth();
  const [mode, setMode] = useState("text"); // text | reference
  const [model, setModel] = useState("nano-banana");
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [quality, setQuality] = useState("medium");
  const [count, setCount] = useState(1);
  const [refPreview, setRefPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ai-studio/history?limit=24`, { credentials: "include" });
      if (r.ok) setHistory(await r.json());
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    if (mode === "reference" && model !== "nano-banana") setModel("nano-banana");
  }, [mode, model]);

  const canGenerate = useMemo(() => {
    if (busy) return false;
    if (prompt.trim().length < 4) return false;
    if (mode === "reference" && !refPreview) return false;
    return true;
  }, [busy, prompt, mode, refPreview]);

  const handleFile = async (e) => {
    setError("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(f.type)) return setError("Reference must be PNG, JPEG or WEBP.");
    if (f.size > MAX_REF_BYTES) return setError("Reference must be ≤4MB.");
    try {
      const { base64, mime } = await readAsBase64(f);
      setRefPreview({ base64, mime, dataUrl: `data:${mime};base64,${base64}`, name: f.name });
    } catch (err) { setError(String(err.message || err)); }
  };
  const clearRef = () => { setRefPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const generate = async () => {
    setError(""); setBusy(true); setImages([]);
    try {
      const body = { model, prompt: prompt.trim(), aspect_ratio: aspectRatio, quality, count };
      if (mode === "reference" && refPreview) {
        body.reference_image_base64 = refPreview.base64;
        body.reference_mime_type = refPreview.mime;
      }
      const r = await fetch(`${API}/api/ai-studio/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: `HTTP ${r.status}` }));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setImages(await r.json());
      await loadHistory();
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-ai-image-studio" style={{ padding: "1.5rem 2rem", minHeight: "100vh", background: "var(--paper)" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid var(--line)", flexWrap: "wrap", gap: ".8rem" }}>
        <div>
          <p className="mono" style={{ fontSize: ".65rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .3rem" }}>
            Admin · AI Image Studio
          </p>
          <h1 style={{ fontFamily: "var(--display)", fontSize: "1.6rem", letterSpacing: "-.01em", margin: 0 }}>
            Generate photorealistic and editorial imagery
          </h1>
        </div>
        <div style={{ display: "flex", gap: ".8rem", alignItems: "center", flexWrap: "wrap" }}>
          <Link to="/admin/leads" className="btn btn-ghost" data-testid="ai-studio-nav-leads" style={{ padding: ".4rem .9rem", fontSize: ".85rem" }}>Leads</Link>
          <Link to="/admin/seo" className="btn btn-ghost" data-testid="ai-studio-nav-seo" style={{ padding: ".4rem .9rem", fontSize: ".85rem" }}>SEO</Link>
          <span style={{ fontSize: ".85rem", color: "var(--ink-soft)" }}>{user?.email}</span>
          <button type="button" onClick={logout} className="btn btn-ghost" data-testid="ai-studio-logout" style={{ padding: ".4rem .9rem", fontSize: ".85rem" }}>Log out</button>
        </div>
      </header>

      {/* Mode toggle */}
      <div style={{ display: "inline-flex", border: "1px solid var(--line)", marginBottom: "1rem" }} role="tablist">
        {[
          { id: "text", label: "Text-to-image" },
          { id: "reference", label: "Image-to-image (reference)" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`ai-studio-mode-${t.id}`}
            onClick={() => setMode(t.id)}
            style={{
              padding: ".55rem 1rem", background: mode === t.id ? "var(--ink)" : "transparent",
              color: mode === t.id ? "var(--paper)" : "var(--ink)", border: 0, cursor: "pointer",
              fontFamily: "var(--mono)", fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 380px) 1fr", gap: "1.5rem" }}>
        {/* Left · Controls */}
        <section style={{ border: "1px solid var(--line)", padding: "1.2rem", background: "#fff" }}>
          <label style={{ display: "block", marginBottom: "1rem" }}>
            <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Model</p>
            <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
              {MODEL_OPTIONS.map((m) => {
                const disabled = mode === "reference" && m.id !== "nano-banana";
                return (
                  <label key={m.id} style={{ display: "flex", gap: ".6rem", padding: ".6rem .8rem", border: `1px solid ${model === m.id && !disabled ? "var(--brass-deep)" : "var(--line)"}`, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .45 : 1 }}>
                    <input type="radio" name="model" value={m.id} checked={model === m.id} onChange={(e) => !disabled && setModel(e.target.value)} disabled={disabled} data-testid={`ai-studio-model-${m.id}`} />
                    <div>
                      <p style={{ margin: 0, fontFamily: "var(--display)", fontSize: ".95rem" }}>{m.label}</p>
                      <p style={{ margin: 0, fontSize: ".78rem", color: "var(--ink-soft)", lineHeight: 1.4 }}>{m.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </label>

          {mode === "reference" && (
            <label style={{ display: "block", marginBottom: "1rem" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Reference image · PNG/JPEG/WEBP ≤4MB</p>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} data-testid="ai-studio-ref-file" style={{ display: "none" }} />
              {refPreview ? (
                <div style={{ border: "1px solid var(--line)", padding: ".4rem", background: "var(--paper-raised)" }}>
                  <img src={refPreview.dataUrl} alt="Reference" style={{ width: "100%", maxHeight: 200, objectFit: "contain", display: "block", background: "#f5f5f4" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: ".4rem", fontSize: ".75rem", color: "var(--ink-soft)" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>{refPreview.name}</span>
                    <div style={{ display: "flex", gap: ".4rem" }}>
                      <button type="button" onClick={() => fileRef.current?.click()} className="btn btn-ghost" style={{ padding: ".2rem .55rem", fontSize: ".75rem" }} data-testid="ai-studio-ref-replace">Replace</button>
                      <button type="button" onClick={clearRef} className="btn btn-ghost" style={{ padding: ".2rem .55rem", fontSize: ".75rem" }} data-testid="ai-studio-ref-clear">Remove</button>
                    </div>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="ai-studio-ref-upload" style={{ width: "100%", padding: "1.4rem", border: "1px dashed var(--line)", background: "var(--paper-raised)", cursor: "pointer", fontFamily: "inherit", color: "var(--ink-soft)" }}>
                  Click to upload a reference image
                </button>
              )}
              <p style={{ margin: ".5rem 0 0", fontSize: ".7rem", color: "var(--ink-soft)", lineHeight: 1.4 }}>
                Nano Banana will preserve the important characteristics of the source product while applying your requested variation (background swap · restyling · lifestyle scene · colour/material variation).
              </p>
            </label>
          )}

          <label style={{ display: "block", marginBottom: "1rem" }}>
            <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Prompt</p>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={mode === "reference"
                ? "Restyle this brass pendant into a bright morning-lit loft with warm tungsten glow, quiet-luxury interior."
                : "A brass pendant lamp with hand-blown clear-glass shade hanging above a solid-sheesham dining table, warm-tungsten light, quiet-luxury interior, natural morning light, photorealistic commercial photography"}
              rows={6}
              data-testid="ai-studio-prompt"
              style={{ width: "100%", padding: ".6rem .8rem", fontFamily: "inherit", fontSize: ".9rem", lineHeight: 1.5, border: "1px solid var(--line)", background: "#fff", resize: "vertical" }}
            />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".6rem", marginBottom: "1rem" }}>
            <label>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Aspect ratio</p>
              <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} data-testid="ai-studio-aspect" style={selectStyle}>
                {ASPECT_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Quality</p>
              <select value={quality} onChange={(e) => setQuality(e.target.value)} data-testid="ai-studio-quality" style={selectStyle}>
                {QUALITY_OPTIONS.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </label>
          </div>

          <label style={{ display: "block", marginBottom: "1.2rem" }}>
            <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Variations (1-4)</p>
            <input type="number" min="1" max="4" value={count} onChange={(e) => setCount(Math.max(1, Math.min(4, Number(e.target.value) || 1)))} data-testid="ai-studio-count" style={selectStyle} />
          </label>

          <button type="button" onClick={generate} disabled={!canGenerate} className="btn btn-primary" data-testid="ai-studio-generate" style={{ width: "100%", padding: ".7rem", fontSize: ".95rem" }}>
            {busy ? "Generating…" : "Generate images"}
          </button>

          {error && <div data-testid="ai-studio-error" style={{ marginTop: "1rem", padding: ".8rem", border: "1px solid #b91c1c", background: "#fef2f2", color: "#7f1d1d", fontSize: ".85rem" }}>{error}</div>}
        </section>

        {/* Right · Results + History */}
        <section>
          <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .6rem" }}>
            {images.length > 0 ? `Latest generation · ${images.length} image(s)` : "No generation yet"}
          </p>
          {busy && (
            <div data-testid="ai-studio-loading" style={{ padding: "2rem", border: "1px dashed var(--line)", textAlign: "center", color: "var(--ink-soft)", marginBottom: "1rem" }}>
              Generating {count} image{count > 1 ? "s" : ""} with {MODEL_OPTIONS.find((m) => m.id === model)?.label}…
            </div>
          )}
          {images.length > 0 && (
            <div data-testid="ai-studio-results" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
              {images.map((img) => (
                <figure key={img.id} data-testid={`ai-studio-result-${img.id}`} style={{ margin: 0, border: "1px solid var(--line)", background: "#fff" }}>
                  <img src={`${API}${img.url}`} alt="Generated" style={{ width: "100%", display: "block", background: "#f5f5f4" }} />
                  <figcaption style={{ padding: ".6rem .8rem", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: ".8rem", color: "var(--ink-soft)" }}>
                    <span>{Math.round(img.size / 1024)} KB · {img.mime_type}</span>
                    <a href={`${API}${img.url}`} download={`${img.id}.png`} target="_blank" rel="noreferrer" className="btn btn-ghost" data-testid={`ai-studio-download-${img.id}`} style={{ padding: ".3rem .7rem", fontSize: ".8rem" }}>Download ↓</a>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}

          {history.length > 0 && (
            <>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "1.5rem 0 .6rem" }}>History · last 24</p>
              <div data-testid="ai-studio-history" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: ".6rem" }}>
                {history.map((h) => (
                  <a key={h.id} href={`${API}${h.url}`} target="_blank" rel="noreferrer" data-testid={`ai-studio-history-${h.id}`} title={h.prompt} style={{ display: "block", border: "1px solid var(--line)", background: "#fff", textDecoration: "none" }}>
                    <img src={`${API}${h.url}`} alt={h.prompt?.slice(0, 60)} style={{ width: "100%", display: "block", aspectRatio: "1", objectFit: "cover", background: "#f5f5f4" }} />
                    <p style={{ margin: 0, padding: ".4rem .5rem", fontSize: ".72rem", color: "var(--ink-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {h.model}{h.has_reference ? " · ref" : ""} · {h.aspect_ratio}
                    </p>
                  </a>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

const selectStyle = { width: "100%", padding: ".5rem .6rem", fontFamily: "inherit", fontSize: ".9rem", border: "1px solid var(--line)", background: "#fff" };

export default AdminAiImageStudio;
